//
import { ColumnDef } from "@tanstack/react-table"
import moment from "moment"
//
import { ChipStyled } from "src/shared/components"
import { capitalizeFirstLetter } from "src/shared/utils/data-helpers"
import { IconButton, Stack, Tooltip } from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import IntegrationIcon from "@mui/icons-material/Extension"
import PaymentIcon from "@mui/icons-material/Payment"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import { AllowedOrderType, IntegrationType } from "src/graphql/quark/quark.type"
import { getDisplayPrice } from "src/shared/utils/helpers-functions"

export const getIntegrationsListTableColumns = ({
  onEdit,
  hasPermission
}: {
  onEdit?: (integration: any) => void
  hasPermission: (permission: string) => boolean
}): ColumnDef<any, any>[] => {
  return [
    {
      header: "Name",
      accessorKey: "name"
    },
    {
      header: "Type",
      accessorKey: "type",
      cell: ({ row }) => (
        <ChipStyled
          label={capitalizeFirstLetter((row.original.type ?? "").toString().toLowerCase())}
          size="small"
          variant="outlined"
          color={(getIntegrationTypeColor(row.original.type) as any) ?? "default"}
        />
      )
    },

    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) => (
        <ChipStyled
          label={capitalizeFirstLetter((row.original.status ?? "").toString().toLowerCase())}
          size="small"
          variant="filled"
          color={(getStatusColor(row.original.status) as any) ?? "default"}
        />
      )
    },
    {
      header: "Allowed Order Type",
      accessorKey: "allowedOrderType",
      cell: ({ row }) => {
        return (
          <Stack direction="row" width="100%" spacing={1}>
            {row?.original?.allowedOrderType?.map((type: IntegrationType) => (
              <ChipStyled
                label={capitalizeFirstLetter((type ?? "").toString().toLowerCase())}
                size="small"
                variant="filled"
                color={(getIntegrationTypeColor(type) as any) ?? "default"}
                icon={getIntegrationTypeIcon(type)}
              />
            ))}
          </Stack>
        )
      }
    },
    {
      header: "Charges",
      accessorKey: "charges",
      cell: ({ row }) => `${getDisplayPrice(row.original.charges || 0, "PKR ")}`
    },
    {
      header: "Created At",
      accessorKey: "createdAt",
      accessorFn: (row) => moment(row?.createdAt).format("lll")
    },
    {
      header: "Updated At",
      accessorKey: "updatedAt",
      accessorFn: (row) => moment(row?.updatedAt).format("lll")
    },
    {
      header: "Actions",
      enableHiding: false,
      meta: {
        textAlign: "center",
        headerAlign: "center",
        width: "100px"
      },
      cell: ({ row }) => (
        <Tooltip title={hasPermission("system:integrations:update") ? "Edit Integration" : "You don't have permission"}>
          <IconButton
            disabled={!hasPermission("system:integrations:update")}
            onClick={() => onEdit?.(row.original)}
            size="small"
            sx={{
              color: "primary.main",
              "&:hover": {
                backgroundColor: "primary.light",
                color: "white"
              }
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    }
  ]
}

const getIntegrationTypeIcon = (type?: string) => {
  switch (type) {
    case AllowedOrderType.Return:
      return <LocalShippingIcon fontSize="small" />
    case AllowedOrderType.Normal:
      return <PaymentIcon fontSize="small" />
    default:
      return <IntegrationIcon fontSize="small" />
  }
}

const getIntegrationTypeColor = (type?: string) => {
  switch (type) {
    case IntegrationType.Fbr:
      return "primary"
    case IntegrationType.Srb:
      return "success"
    default:
      return "default"
  }
}

const getStatusColor = (status?: string) => {
  switch (status) {
    case "ACTIVE":
      return "success"
    case "INACTIVE":
      return "warning"
    case "PENDING":
      return "info"
    case "ERROR":
      return "error"
    default:
      return "default"
  }
}

// const getOrderTypeColor = (orderType?: AllowedOrderType) => {
//   switch (orderType) {
//     case AllowedOrderType.Return:
//       return "primary"
//     case AllowedOrderType.Normal:
//       return "info"
//     default:
//       return "default"
//   }
// }
